import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/Header';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

function DisclosurePage() {
  const { slug } = useParams();
  const [disclosureData, setDisclosureData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDisclosure();
  }, [slug]);

  const fetchDisclosure = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/disclosure/${slug}`);
      
      if (!response.ok) {
        throw new Error('Disclosure not found');
      }
      
      const data = await response.json();
      setDisclosureData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-white text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
        <Header />
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">Disclosure Not Found</h1>
            <p className="text-slate-300 mb-8">{error}</p>
            <Link 
              to={`/apps/${slug}`}
              className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to App
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <Header />
      
      <main className="container mx-auto px-4 py-16 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to={`/apps/${slug}`}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to {disclosureData?.name}
          </Link>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
              <AlertTriangle className="w-6 h-6 text-amber-400" />
            </div>
            <h1 className="text-4xl font-bold text-white">
              {disclosureData?.name} – AI Disclosures
            </h1>
          </div>
          
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
            <p className="text-amber-200 text-sm font-medium">
              ⚠️ <strong>Important:</strong> Please read this disclosure carefully before using AI-powered features. 
              This document explains the limitations of AI systems and your responsibilities when using them.
            </p>
          </div>
        </div>

        {/* Disclosure Content */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-8">
          <div className="prose prose-invert prose-slate max-w-none">
            <ReactMarkdown
              components={{
                h1: ({ children }) => <h1 className="text-3xl font-bold text-white mb-6 border-b border-slate-700 pb-3">{children}</h1>,
                h2: ({ children }) => <h2 className="text-2xl font-bold text-white mt-8 mb-4">{children}</h2>,
                h3: ({ children }) => <h3 className="text-xl font-semibold text-amber-300 mt-6 mb-3">{children}</h3>,
                p: ({ children }) => <p className="text-slate-300 mb-4 leading-relaxed">{children}</p>,
                ul: ({ children }) => <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2 ml-4">{children}</ul>,
                li: ({ children }) => <li className="text-slate-300">{children}</li>,
                strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
                hr: () => <hr className="border-slate-700 my-8" />,
              }}
            >
              {disclosureData?.disclosureText}
            </ReactMarkdown>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center space-y-4">
          <Link 
            to={`/apps/${slug}`}
            className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to {disclosureData?.name}
          </Link>
          
          <div className="text-sm text-slate-400">
            <Link to={`/privacy/${slug}`} className="hover:text-white transition-colors">
              Privacy Policy
            </Link>
            {" · "}
            <Link to={`/terms/${slug}`} className="hover:text-white transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/[0.06] bg-slate-950 mt-16">
        <div className="mx-auto max-w-7xl px-6 py-10 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_c83bb9d1-4481-4216-be0b-e2d2e530a3a4/artifacts/j5gcpl1b_Peaceful%20harmony%20in%20a%20logo%20design.png"
              alt="Perfect Peace Enterprise Logo"
              className="h-10 w-auto object-contain"
            />
            <div>
              <div className="text-base font-semibold text-white">
                Perfect Peace Enterprise
              </div>
              <p className="mt-0.5 text-xs text-slate-500">
                Building digital products for real-world impact.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-6 text-sm text-slate-500">
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <Link to="/privacy" className="hover:text-white transition-colors">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-white transition-colors">
              Terms
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default DisclosurePage;
